import * as React from "react";
import { CalendarDays } from "lucide-react";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

// Indian-format date field: shows/accepts dd/mm/yyyy, emits ISO yyyy-mm-dd via
// onChange({ target: { value } }) so it is a drop-in for <input type="date">.

const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})/;
const pad = (n) => String(n).padStart(2, "0");
const toIso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const toDisplay = (iso) => {
  const m = ISO_RE.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};
const toDate = (iso) => {
  const m = ISO_RE.exec(iso || "");
  return m ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])) : undefined;
};
const parseDisplay = (t) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(t);
  if (!m) return null;
  const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
  if (d.getDate() !== Number(m[1]) || d.getMonth() !== Number(m[2]) - 1) return null;
  return toIso(d);
};
const maskDigits = (raw) => {
  const d = raw.replace(/\D/g, "").slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join("/");
};

export const DateInput = React.forwardRef(function DateInput(
  { value, onChange, className, min, max, disabled, required, id, name, placeholder = "dd/mm/yyyy", style, ...rest },
  ref,
) {
  const [text, setText] = React.useState(toDisplay(value));
  const [open, setOpen] = React.useState(false);
  const [invalid, setInvalid] = React.useState(false);
  React.useEffect(() => { setText(toDisplay(value)); setInvalid(false); }, [value]);

  const inRange = (iso) => (!min || iso >= min) && (!max || iso <= max);
  const emit = (iso) => onChange?.({ target: { value: iso, name, id }, currentTarget: { value: iso } });

  const handleType = (e) => {
    const raw = e.target.value;
    if (ISO_RE.test(raw) && raw.length === 10) { setText(toDisplay(raw)); emit(raw); return; }
    const masked = maskDigits(raw);
    setText(masked);
    if (!masked) emit("");
    const iso = masked.length === 10 ? parseDisplay(masked) : null;
    setInvalid(masked.length === 10 && !(iso && inRange(iso)));
    if (iso && inRange(iso)) emit(iso);
  };
  const handleBlur = () => {
    if (!text) return;
    const iso = parseDisplay(text);
    if (!iso || !inRange(iso)) { setText(toDisplay(value)); setInvalid(false); }
  };

  return (
    <div className="relative w-full" style={style}>
      <input
        ref={ref}
        id={id}
        name={name}
        type="text"
        inputMode="numeric"
        autoComplete="off"
        placeholder={placeholder}
        value={text}
        onChange={handleType}
        onBlur={handleBlur}
        disabled={disabled}
        required={required}
        aria-label={rest["aria-label"] || placeholder}
        aria-invalid={invalid || undefined}
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 pr-9 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
          invalid && "border-red-500 focus-visible:ring-red-500",
          className,
        )}
        {...rest}
      />
      {invalid && (
        <div className="absolute left-0 top-full mt-0.5 text-[11px] text-red-600 whitespace-nowrap z-10" role="alert" data-testid="date-input-invalid">
          Not a valid date{min || max ? " in the allowed range" : ""}
        </div>
      )}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label="Open calendar"
            className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100 disabled:opacity-40"
            data-testid={rest["data-testid"] ? `${rest["data-testid"]}-calendar` : undefined}
          >
            <CalendarDays size={15} />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="end">
          <Calendar
            mode="single"
            selected={toDate(value)}
            defaultMonth={toDate(value)}
            onSelect={(d) => { if (d) { emit(toIso(d)); setOpen(false); } }}
            disabled={[min && { before: toDate(min) }, max && { after: toDate(max) }].filter(Boolean)}
            captionLayout="dropdown-buttons"
            fromYear={1940}
            toYear={new Date().getFullYear() + 5}
            initialFocus
            classNames={{
              caption_label: "hidden",
              caption_dropdowns: "flex items-center gap-1",
              dropdown: "h-7 rounded-md border border-zinc-200 bg-white px-1.5 text-sm cursor-pointer",
              dropdown_icon: "hidden",
              vhidden: "sr-only",
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
});
